import type { ResultsWidgetRuntimeState } from './ResultsSelfOwnedModel';
import { Duration } from '@js-joda/core';
import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type {
    ColumnOptions,
    OutputColumnWithMetadata,
    OutputFilter,
    SortModel,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import { getClientColumnDefinition } from 'src/features/clientColumns';
import {
    buildResultsTableQueryOptions,
    clientSortModelToSortModel,
} from 'src/features/table/hooks/useResultsTableQueryOptions';
import { columnDefinitionKey } from 'src/features/table/stitching/regularColumnsQuery';
import { planStitchedColumns } from 'src/features/table/stitching/stitchedColumnPlan';
import type { StitchedColumnPlan } from 'src/features/table/stitching/stitchedColumnPlan';
import type { ClientSortModel } from 'src/features/table/types';
import type { SlugMapDict } from 'src/lib/dictionary/dictionary';
import type { TableModel } from 'src/models/TableModel';
import { makeColumnTemplateEqualityKey } from 'src/types';
import type { FetchableTableCreationResultWithColumns } from 'src/types';

export interface ResultsTableModelInit {
    table: TableModel;
    logger: Logger;
    devtoolsName: string;
    /** Input readiness from the self-owned model; the query stays gated until `ready`. */
    inputState: ReadonlySignal<ResultsWidgetRuntimeState>;
    columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    fastIdeas: ReadonlySignal<IdeaDTO<IdeaPlanDTO>[]>;
    slowIdea: ReadonlySignal<IdeaDTO<IdeaPlanDTO> | null>;
    fastIdeasCacheKey: ReadonlySignal<string>;
    slowIdeaCacheKey: ReadonlySignal<string>;
    clientSortModel: ReadonlySignal<ClientSortModel>;
    scrolling: ReadonlySignal<boolean>;
    stickyFilterValue: ReadonlySignal<string>;
    columnOptions: ReadonlySignal<Partial<ColumnOptions>>;
    validationModeEnabled: ReadonlySignal<boolean>;
    dwellTimeSeconds: ReadonlySignal<number>;
    userViewKey: ReadonlySignal<string>;
    /**
     * Client-stitched columns (ADR 0020): the table becomes a keys table holding only the symbol
     * and sorted columns, and stays disabled until the slug map can plan it. Omit for a full table.
     */
    stitching?: { readonly slugMap: ReadonlySignal<SlugMapDict> };
}

export interface ResultsTableModelShape {
    /** Combined runtime state: input resolution plus table-run failures. */
    readonly state: ReadonlySignal<ResultsWidgetRuntimeState>;
    /** Validation columns returned by table-creation metadata. */
    readonly validationColumns: ReadonlySignal<OutputColumnWithMetadata[]>;
    /** The stitched column plan; null without stitching or while it cannot be planned yet. */
    readonly stitchedPlan: ReadonlySignal<StitchedColumnPlan | null>;
}

/**
 * Owns the Results table query lifecycle: it initializes the injected
 * TableModel with reactive query options that stay disabled until every
 * input (column template, fast/slow idea DTOs, initial sort) is resolved.
 * The first enabled query is therefore also the final one for the loaded
 * state — one table creation, no template-resolved-late or
 * ideas-arrived-late recreations.
 */
export const ResultsTableModel: ModelConstructor<ResultsTableModelShape, [ResultsTableModelInit]> = createModel<
    ResultsTableModelShape,
    [ResultsTableModelInit]
>((init: ResultsTableModelInit) => {
    const validationColumns: Signal<OutputColumnWithMetadata[]> = signal([]);

    // Memoized sort: recomputations that produce an equal sort must return
    // the prior reference, or every query-options consumer re-fires on
    // semantically-unchanged sorts (fresh-object derivations can even trip
    // signals cycle detection through TableModel's options watcher). A
    // closure memo keeps this a pure derivation instead of an effect-fed
    // shadow signal.
    let lastSort: SortModel | undefined;
    const sort: ReadonlySignal<SortModel | undefined> = computed(() => {
        const next = clientSortModelToSortModel(init.clientSortModel.value, init.scrolling.value);
        if (!isEqual(next, lastSort)) {
            lastSort = next;
        }
        return lastSort;
    });

    // Memoized like the sort: an equal plan keeps its reference, so stitched bindings and the
    // keys query see no change.
    let lastPlan: StitchedColumnPlan | null = null;
    const stitchedPlan: ReadonlySignal<StitchedColumnPlan | null> = computed(() => {
        const stitching = init.stitching;
        const template = init.columnTemplate.value;
        const next =
            stitching && template
                ? planStitchedColumns(
                      template.columns,
                      sort.value,
                      stitching.slugMap.value,
                      (clientKey) => getClientColumnDefinition(clientKey)?.requiredSlugs ?? [],
                  )
                : null;
        if (!isEqual(next, lastPlan)) {
            lastPlan = next;
        }
        return lastPlan;
    });

    /** The template the server table is built from: the keys columns when stitching. */
    const serverTemplate = computed((): Pick<ColumnTemplate, 'columns'> | null => {
        const template = init.columnTemplate.value;
        if (!init.stitching || !template) {
            return template;
        }
        const plan = stitchedPlan.value;
        return plan ? { columns: [...plan.keysColumns] } : null;
    });

    const columnTemplateRunKey = computed(() => {
        const template = init.columnTemplate.value;
        if (init.stitching) {
            // Keyed by what the keys table computes, so showing, hiding, or adding stitched columns
            // keeps the keys table.
            return serverTemplate.value?.columns.map(columnDefinitionKey).sort().join('||');
        }
        return template ? makeColumnTemplateEqualityKey(template) : undefined;
    });

    const tableQueryOptions = computed(() => {
        const inputState = init.inputState.value;
        const columnTemplate = serverTemplate.value;

        if (inputState.type !== 'ready' || !columnTemplate) {
            return {
                queryKey: ['results-widget-table', 'initializing'] as const,
                queryFn: async () => [null, []] as [null, OutputColumnWithMetadata[]],
                enabled: false,
                staleTime: 0,
                gcTime: 0,
            };
        }

        const filterValue = init.stickyFilterValue.value;
        const filter: OutputFilter | undefined = filterValue ? { type: 'search', value: filterValue } : undefined;
        const scrolling = init.scrolling.value;
        const dwellSeconds = init.dwellTimeSeconds.value;
        const fastIdeaDwellTime = scrolling && dwellSeconds > 0 ? Duration.ofSeconds(dwellSeconds) : undefined;

        return buildResultsTableQueryOptions({
            slowIdea: init.slowIdea.value ?? undefined,
            fastIdeas: init.fastIdeas.value,
            fastIdeaDwellTime,
            validationModeEnabled: init.validationModeEnabled.value,
            columnTemplate,
            useScrolling: scrolling,
            filter,
            userViewKey: init.userViewKey.value,
            ideaUserViewKeyScope: 'none',
            columnOptions: init.columnOptions.value,
            log: init.logger,
            fastIdeasCacheKey: init.fastIdeasCacheKey.value,
            slowIdeaCacheKey: init.slowIdeaCacheKey.value,
            columnTemplateRunKey: columnTemplateRunKey.value,
            sort: sort.value,
        });
    });

    // Validation columns ride along in the query payload; peel them off so
    // the TableModel only sees the table-creation result.
    const tableOnlyQueryOptions = computed(() => {
        const opts = tableQueryOptions.value;
        return {
            ...opts,
            select: (data: [FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]) => {
                if (data[1]) {
                    validationColumns.value = data[1];
                }
                return data[0];
            },
        };
    });

    init.table.init({
        queryOptions: tableOnlyQueryOptions,
        initBounds: { firstRow: 0, windowSize: 100 },
        devtoolsName: init.devtoolsName,
    });

    const state: ReadonlySignal<ResultsWidgetRuntimeState> = computed(() => {
        const inputState = init.inputState.value;
        if (inputState.type !== 'ready') {
            return inputState;
        }
        if (init.table.failureCount.value > 0 && init.table.error) {
            return { type: 'error', stage: 'table', error: init.table.error };
        }
        return { type: 'ready' };
    });

    return {
        state,
        validationColumns: computed(() => validationColumns.value),
        stitchedPlan,
    };
});

export type ResultsTableModel = Model<ResultsTableModelShape>;
